// Tipos para el sistema de autenticación y autorización

export type UserRole = 'superadmin' | 'admin' | 'comisario' | 'usuario';

export interface UserProfile {
  uid: string;
  email: string;
  displayName?: string;
  photoURL?: string;
  role: UserRole;
  permissions: string[];
  createdAt: string;
  lastLogin?: string;
}

export interface AuthContextType {
  currentUser: UserProfile | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  hasPermission: (permission: string) => boolean;
  hasAnyRole: (roles: UserRole[]) => boolean;
}

// Permisos por ruta
// Cada ruta de la aplicación debe tener su permiso correspondiente
export const PERMISSIONS = {
  // Menú principal - usando los nombres antiguos de permisos para compatibilidad
  VIEW_LIGAS: 'view:leagues',
  VIEW_CARRERAS: 'view:races',
  VIEW_CLASIFICACIONES: 'view:standings',
  VIEW_LASTRES: 'view:lastres',
  VIEW_EQUIPOS: 'view:teams',
  VIEW_PILOTOS: 'view:drivers',
  VIEW_SANCIONES: 'view:penalties',
  VIEW_REGLAS_AUTOMATICAS: 'view:penalty_rules',
  VIEW_RECLAMACIONES: 'view:claims',
  VIEW_APELACIONES: 'view:appeals',
  VIEW_EVENTOS: 'view:events',
  VIEW_PUNTUACIONES: 'view:points',
  VIEW_GUIA: 'view:guide',
  VIEW_GENERADOR_MINIATURAS: 'view:thumbnails_generator',
  VIEW_BLOG: 'view:blog',
  
  // Acciones
  MANAGE_LIGAS: 'manage:leagues',
  MANAGE_CARRERAS: 'manage:races',
  MANAGE_EQUIPOS: 'manage:teams',
  MANAGE_PILOTOS: 'manage:drivers',
  MANAGE_SANCIONES: 'manage:penalties',
  MANAGE_REGLAS_AUTOMATICAS: 'manage:penalty_rules',
  MANAGE_RECLAMACIONES: 'manage:claims',
  MANAGE_APELACIONES: 'manage:appeals',
  MANAGE_EVENTOS: 'manage:events',
  MANAGE_PUNTUACIONES: 'manage:points',
  
  // Administración
  ADMIN_USUARIOS: 'admin:users',
  ADMIN_PERMISOS: 'admin:permissions',
} as const;

// Mapeo de roles a permisos
export const ROLE_PERMISSIONS: Record<UserRole, string[]> = {
  superadmin: [
    'admin:users',
    'admin:permissions',
    'manage:races',
    'manage:leagues',
    'manage:drivers',
    'manage:teams',
    'view:penalties',
    'manage:penalties',
    'manage:appeals',
    'view:standings',
    'view:races',
    'view:drivers',
    'view:teams',
    'view:leagues',
    'manage:penalty_rules',
    'view:penalty_rules',
    'manage:claims',
    'view:claims',
    'view:appeals',
    'manage:events',
    'view:events',
    'manage:points',
    'view:points',
    'view:guide',
    'view:lastres',
    'view:ia_redaccion_circuitos',
    'view:warmup',
    'view:thumbnails_generator',
    'view:blog'
  ],
  admin: [
    'view:leagues',
    'view:races',
    'view:standings',
    'view:lastres',
    'view:teams',
    'view:drivers',
    'view:penalties',
    'view:penalty_rules',
    'view:claims',
    'view:appeals',
    'view:events',
    'view:points',
    'view:guide',
    'manage:leagues',
    'manage:races',
    'manage:teams',
    'manage:drivers',
    'manage:penalties',
    'manage:penalty_rules',
    'manage:claims',
    'manage:appeals',
    'manage:events',
    'manage:points',
    'admin:users',
    'view:ia_redaccion_circuitos',
    'view:warmup',
    'view:thumbnails_generator',
    'view:blog'
  ],
  comisario: [
    'view:leagues',
    'view:races',
    'view:standings',
    'view:teams',
    'view:drivers',
    'view:penalties',
    'view:claims',
    'view:appeals',
    'manage:penalties',
    'manage:claims',
    'manage:appeals',
    'view:blog'
  ],
  usuario: [
    'view:guide'          // Ver Guía (único permiso por defecto para nuevos usuarios)
    // Los siguientes permisos están comentados para que los nuevos usuarios no tengan acceso
    // 'view:claims',      // Gestionar Reclamaciones
    // 'view:events',      // Ver Eventos
    // 'view:points',      // Ver Puntuaciones
    // 'view:leagues',     // Ver Ligas
    // 'view:races',       // Ver Carreras
    // 'view:standings',   // Ver Clasificaciones
    // 'view:lastres',     // Ver Lastres
    // 'view:teams',       // Ver Equipos
    // 'view:drivers',     // Ver Pilotos
    // 'view:penalties',   // Ver Sanciones
  ]
};

// Configuración de rutas protegidas
export const ROUTE_PERMISSIONS: Record<string, string> = {
  // Rutas de visualización
  '/ligas': 'view:leagues',
  '/carreras': 'view:races',
  '/clasificaciones': 'view:standings',
  '/lastres': 'view:lastres',
  '/equipos': 'view:teams',
  '/socios': 'view:drivers',
  '/sanciones': 'view:penalties',
  '/sanciones/reglas-automaticas': 'view:penalty_rules',
  '/reclamaciones': 'view:claims',
  '/apelaciones': 'view:appeals',
  '/eventos': 'view:events',
  '/puntuaciones': 'view:points',
  '/guia-bienvenida': 'view:guide',
  '/ia/redaccion-circuitos': 'view:ia_redaccion_circuitos',
  '/warmup': 'view:warmup',
  '/miniaturas/generador-miniaturas': 'view:thumbnails_generator',
  '/blog': 'view:blog',
  
  // Rutas de gestión
  '/ligas/nueva': 'manage:leagues',
  '/ligas/editar': 'manage:leagues',
  '/carreras/nueva': 'manage:races',
  '/carreras/editar': 'manage:races',
  '/equipos/nuevo': 'manage:teams',
  '/equipos/editar': 'manage:teams',
  '/socios/nuevo': 'manage:drivers',
  '/socios/editar': 'manage:drivers',
  '/sanciones/nueva': 'manage:penalties',
  '/sanciones/editar': 'manage:penalties',
  '/sanciones/reglas-automaticas/editar': 'manage:penalty_rules',
  '/reclamaciones/nueva': 'manage:claims',
  '/reclamaciones/editar': 'manage:claims',
  '/apelaciones/editar': 'manage:appeals',
  '/eventos/nuevo': 'manage:events',
  '/eventos/editar': 'manage:events',
  '/puntuaciones/editar': 'manage:points',
  
  // Rutas de administración
  '/admin/usuarios': 'admin:users',
  '/admin/permisos': 'admin:permissions',
  '/pilotos/editar': 'manage:drivers',
};
